// Psychological Skills. Cert masterclass (U8L2).
// Mux: UVfezzZjVnoISKvfd6KWGR7sfozhfJnwdiLupa9PSRc
//
// Authored from the talk's auto-caption transcript
// (knowledge-base/transcripts/cert/Psychological-Skills.md).
// Quotes are lifted from what Coleman actually said. Bullets distill the
// practical takeaway. Coleman's voice, plain language, no em dashes.

export const PSYCHOLOGICAL_SKILLS_MODULE_KEY = "psychological-skills";

const VIDEO = "UVfezzZjVnoISKvfd6KWGR7sfozhfJnwdiLupa9PSRc";

export const PSYCHOLOGICAL_SKILLS_MODULE = {
  key: PSYCHOLOGICAL_SKILLS_MODULE_KEY,
  title: "Psychological Skills",
  sub: "Nine psychological qualities you can actually train in your players, and the concrete ways to build each one in the gym.",
  coverVideoId: VIDEO,
  duration: "Full talk plus a guided breakdown",
  chapters: [
    {
      id: "watch",
      title: "Watch the talk",
      blocks: [
        { kind: "text", md:
          "Watch it once start to finish, then come back through these sections for the ideas worth keeping. Coleman runs through nine psychological qualities you can develop with players, rapid fire, one at a time. None of these are quick band-aids. They take time, they take questioning, and they take getting to know the human in front of you. But move even one or two of these per player and the results stack up." },
        { kind: "video", muxPlaybackId: VIDEO,
          caption: "Psychological Skills." },
        { kind: "callout", tone: "tip", title: "Start with the right frame", body:
          "A lot of this is genetic or how a player grew up. Some players just have it. Take the pressure off yourself to get everybody to the top of the mountain, and you will develop it better. Keep a growth mindset and incrementally raise each of these. You do not need to build the next Edwards or the next Kobe competitor to make a player meaningfully better." },
      ],
    },

    {
      id: "confidence",
      title: "1. Confidence",
      blocks: [
        { kind: "text", md:
          "Hard not to put this number one. Confidence breaks into three subcategories, and they are not perfectly correlated with each other, so it helps to know which one you are looking at." },
        { kind: "principles", items: [
          { n: 1, title: "Baseline belief in oneself", body:
            "Just trusting that you are good at something, that you can do it well. Some of the best players have borderline, if not past borderline, delusional belief in themselves. Much of it is personality, but it can be trained by working through challenges over and over and proving to yourself you can conquer them." },
          { n: 2, title: "External confidence", body:
            "The energy, the swag, the vibe of confidence a player gives off. Not perfectly tied to the inner belief, but with good players you can see it, and it is something you can cue with athletes." },
          { n: 3, title: "Perseverance confidence", body:
            "Through challenges, through negativity, through difficult situations, can you stay confident? The first two are acute, how much confidence you have right now. This one is about how long and through how many challenges you can sustain it." },
        ]},
        { kind: "callout", tone: "tip", title: "More on this coming", body:
          "Confidence gets its own full lesson next, including working through challenges and understanding perspective. Treat this as the map, not the deep dive." },
      ],
    },

    {
      id: "game-self-analysis",
      title: "2. Game and self analysis",
      blocks: [
        { kind: "text", md:
          "Being a student of the game. How well can a player analyze themselves and the game on a deeper level to breed improvement? Not every great player thinks about it this deeply. Some just hoop and they are great. But if they did think about it, that multiplier could take them to a new level." },
        { kind: "principles", items: [
          { n: 1, title: "Create dialogue in training", body:
            "Ask players questions instead of telling them do this, this, this. In this situation, with these constraints, what can you do to succeed here? Force them to think and make an answer, then help them through it. Over time that conditions them to think about the game deeper on their own." },
          { n: 2, title: "Make film fun", body:
            "Make it engaging instead of showing things while they fall asleep. Ask questions, watch players they enjoy watching or their own film, joke around. You want them to pick up the habit of watching film because it is engaging, not because you forced the boring on them." },
          { n: 3, title: "Set the question quota", body:
            "An easy standard: you have to ask five good questions today in your training. Whatever they want to ask is fine. It is a constraint that forces them to think deeper and analyze the game and themselves." },
        ]},
        { kind: "pullquote", md:
          "We can say if you want to be a good player you've got to embrace the boring things. I agree with that, but as a coach that's a cop out a lot of times for us not being able to make things engaging." },
        { kind: "checklist", id: "analysis-actions", title: "Build the student of the game", items: [
          "Ask questions in training instead of handing out instructions",
          "Run film sessions that are engaging, not a lecture they sleep through",
          "Set a question quota so they have to think before they leave",
          "Use the games approach so they have to read and decide",
        ]},
      ],
    },

    {
      id: "flow-state",
      title: "3. Flow state accessibility",
      blocks: [
        { kind: "text", md:
          "Maybe the most important one, though it is also a byproduct of confidence, nervous system control, and the rest. Flow state is the mental state where you feel like you can't miss, where you are making every right play and every right decision. The best players access it pretty much game to game." },
        { kind: "pullquote", prominent: true, md:
          "If you're able to condition players to entering the flow state even just a little bit on a game to game basis, that can change everything." },
        { kind: "principles", items: [
          { n: 1, title: "Build flow into workouts", body:
            "Not easy. It takes the workout being difficult, it takes them devoting attentional currency to it, and it takes a difficult goal they are working toward. Get them accessing flow most workouts and they develop the skill of entering it, so in a game it is easier." },
          { n: 2, title: "Meditation", body:
            "A big one you will work on in the mental skills academy. Practice entering the flow state in whatever you are doing, taking notes, building trainings, playing and experimenting, even business work." },
          { n: 3, title: "Learn it yourself first", body:
            "If you understand how to enter the flow state yourself, it helps you talk to players about it, put them in situations to find it, and make it more of a norm for them." },
        ]},
      ],
    },

    {
      id: "relationships-communication",
      title: "4. Relationship management and communication",
      blocks: [
        { kind: "text", md:
          "We all know good players who cannot communicate. But in general, being cool with everyone on your team, being amicable, communicating well, and diffusing situations helps a player succeed a lot." },
        { kind: "principles", items: [
          { n: 1, title: "Put them in situations to work things out", body:
            "Give them something to organize, then give feedback on the communication. Eighteen players, go make three even teams, you have one minute. Watch who goes quiet and who organizes but gets mad, then coach it: be more composed, or speak up and give your input more." },
          { n: 2, title: "Help them understand why relationships matter", body:
            "How vital quality relationships are with everyone around you. Even with someone you do not like, nurture it enough to be sustainable, do not be an energy vacuum that kills the vibe." },
          { n: 3, title: "Teach them relationships go up and down", body:
            "If you work out with another player consistently, sometimes you compete and hate each other, then days later you are best friends again. Understanding that insulates them from spiraling after a hard, competitive day." },
          { n: 4, title: "Mind the coach player dynamic", body:
            "You do not have to be buddy-buddy, but keep in mind the relationship you have with each player in the gym, even a brand new one. That is of massive importance." },
          { n: 5, title: "Ask them tough questions", body:
            "What's holding you back in life right now? What do you need to do as a player to reach the next level, and get specific? The more they grapple with and express tough concepts, the better." },
        ]},
        { kind: "assess", id: "communication-check", prompt:
          "How often do your sessions force players to communicate and work things out with each other, rather than just running your instructions?",
          lowLabel: "Almost never, I run it all", highLabel: "Constantly, they organize themselves" },
      ],
    },

    {
      id: "nervous-system-control",
      title: "5. Nervous system control",
      blocks: [
        { kind: "text", md:
          "Pre-game and in the game. Your nervous system has two sides. **Sympathetic** is what hypes you up, amped and ready to go. **Parasympathetic** is calm, relaxed, ready to sleep or take a hot shower. Most players are very sympathetic before games and very parasympathetic before workouts." },
        { kind: "text", md:
          "Nervous system control is being able to bring that whole range of arousal back to the middle, to a baseline. Bring it up out of the lethargic parasympathetic state for a workout, and bring it down from the crazy amped sympathetic state before a game." },
        { kind: "principles", items: [
          { n: 1, title: "Expose them to the feelings", body:
            "Get them super hyped with music, fast sprints, racing, physicality, a little nervous trying something new, even a little shit talk. That builds awareness for what the sympathetic state actually feels like." },
          { n: 2, title: "Teach them the signs", body:
            "Sympathetic: tunnel vision, driving out of control, no pace, jittery, sailing your shots. Parasympathetic: lethargic defensive possessions, not talking, looking around, low speed, just going through the motions. When they can name the state, they can act on it." },
          { n: 3, title: "Arm them with levers", body:
            "To calm down: breathing, perspective, label it excitement not nerves, feel like you are playing in slow motion, like your game got filmed and put on 25 percent speed. To rev up: a full three-second sprint, recall a motivating thought, remember your why, remember somebody who talked shit weeks ago." },
        ]},
        { kind: "pullquote", md:
          "Try to feel like you're playing in slow motion right now, just like they took your game, they filmed it and they put it on 25 percent speed." },
      ],
    },

    {
      id: "composure-pressure",
      title: "6. Composure in pressure situations",
      blocks: [
        { kind: "text", md:
          "First, help them play to win, not to lose. Shoot every shot like a game winner. Don't focus on what happens if you lose the drill or miss the shot. Try to make the shot, do not try to not miss. But with a checked ego of what it really takes, so you avoid hero ball." },
        { kind: "pullquote", md:
          "Don't focus on what's going to happen if you lose this drill or miss a shot. Just try to do it to win. Don't try to not miss." },
        { kind: "principles", items: [
          { n: 1, title: "Simulate late game", body:
            "Run late-game situations daily or a few times a week, even 3v3 or 1v1, so they understand time and score implications and get used to the feeling of playing late." },
          { n: 2, title: "Let them learn implicitly", body:
            "Explicitly learned skills break down easier under pressure. A pull-up taught as put your foot here, hang in the air, elbow right here will choke easier than one built in a constraint-based, contested drill. Build skills implicitly where you can." },
          { n: 3, title: "Micro-dose pressure daily", body:
            "Even a little pressure in practice scales up to bigger pressure. First to seven threes, miss two in a row and you are off, two players going at it for bragging rights, make three in a row for a point. Small stakes bridge the gap to the real thing." },
        ]},
        { kind: "callout", tone: "tip", title: "Why micro-dosing works", body:
          "Studies show a little pressure in a practice setting applies to higher amounts. Beating your buddy in a workout is not game seven of the Finals, but once you get used to shooting with nerves and overthinking a little, it is scalable up. It bridges the gap to the game." },
      ],
    },

    {
      id: "adaptability",
      title: "7. Adaptability",
      blocks: [
        { kind: "text", md:
          "If their ankle hurts, the floor is slippery, or a teammate goes down, can they just figure it out and win, individually and as a team? That ability to make it work no matter what happens is a massive sign of a great athlete." },
        { kind: "principles", items: [
          { n: 1, title: "Talk about why it matters", body:
            "If you hurt your ankle, don't complain, figure it out. When you drop a tough constraint and a player starts complaining, that is the moment: if you only want to succeed in perfect situations, that's fine, but the best players don't do that. It pushes them to go solve it." },
          { n: 2, title: "Provide constant constraints", body:
            "Give them environmental and other constraints all the time so they get accustomed to working through them and adapting to any situation." },
          { n: 3, title: "Make it cool and make it the norm", body:
            "Our club is literally called Adapt Academy. Celebrate it when a player adapts well, even off the court. Make adapting to tough situations cool and normal, and players stop relying on perfection." },
        ]},
        { kind: "pullquote", prominent: true, md:
          "If you find ways to make adapting to tough situations not only cool but the norm, then players stop relying on perfection. That's a huge switch in a player's mind." },
      ],
    },

    {
      id: "nerves-to-excitement",
      title: "8. Converting nerves to excitement",
      blocks: [
        { kind: "text", md:
          "Part of the IT factor. Expose a player to the idea that it is not nerves, it is your body being amped up for something, your sympathetic nervous system responding because you are excited. That is exactly what it is: excitement and passion." },
        { kind: "pullquote", md:
          "Before an interview, if somebody literally says I'm excited, I'm hyped for this, versus saying I'm nervous, their performance goes up. That's in sports, in interviews, in tests, in anything." },
        { kind: "text", md:
          "The application is simple. Before a game, tell them to mention that they are excited. Even just asking, you hyped for this game, and getting a yeah I'm hyped, reframes the feeling as excitement instead of nerves a little, and their performance will probably go up. Be conscious about doing this and you hand them a psychological tool for every performance." },
        { kind: "checklist", id: "excitement-actions", title: "Reframe the feeling", items: [
          "Teach them the amped feeling is excitement, not nerves",
          "Cue I'm excited or I'm hyped before games and big reps",
          "Ask you hyped for this? so they say it out loud themselves",
          "Make it a habit before every performance, not a one-off pep talk",
        ]},
      ],
    },

    {
      id: "embracing-challenge",
      title: "9. Embracing challenge",
      blocks: [
        { kind: "text", md:
          "Every player with an it factor does this consistently: they select challenge autonomously. They do not just grind through a workout, they search challenge out. You can reverse engineer that for players who do not do it well yet." },
        { kind: "principles", items: [
          { n: 1, title: "Let them choose the challenge", body:
            "Instead of you've got four down and backs, try I'd say four, but how many do you want? If they pick two, to me you're ducking it, you're not a real competitor. If they pick six, that's what we want. Make selecting challenge the example, and let others join in." },
          { n: 2, title: "Help them set their own goals", body:
            "What's your goal here, what do you have to beat to move on? If they lowball it, four out of ten, push them: come on, you can make seven out of ten. There won't always be a coach to challenge them, so they need the rep of choosing it themselves." },
          { n: 3, title: "Make challenge the norm of every workout", body:
            "Everything should be challenging so they get used to getting through difficult situations. Most workouts are too easy skill-wise, so players never learn to work through challenge. Make it the standard and they carry it to any gym, any game." },
        ]},
        { kind: "callout", tone: "warn", title: "The foundation under all of it", body:
          "The underlying foundation behind everything here is psychological safety to fail. If players feel like crap every time they fail, because teammates make fun of them or you treat failure as the ultimate negative, they will never autonomously choose hard challenges. Make failure okay as long as they push through, and that is where you see players choose challenge and get back up fast." },
        { kind: "pullquote", prominent: true, md:
          "The underlying foundation under all of this is the psychological safety for them to fail." },
        { kind: "multipick", id: "psych-focus", prompt:
          "Of these nine, which one or two will you actually start training with your players this month?",
          options: [
            "Confidence",
            "Game and self analysis",
            "Flow state accessibility",
            "Relationship management and communication",
            "Nervous system control",
            "Composure in pressure situations",
            "Adaptability",
            "Converting nerves to excitement",
            "Embracing challenge",
          ]},
        { kind: "recap", id: "recap", title: "Your reflections from this masterclass" },
      ],
    },
  ],
};
